import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Link, Stack, Typography } from '@mui/material';
import { PhxBadge } from '_lex/_atoms/PhxBadge';

import { AppContext } from '../../../../context';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import useOrganizationUser from 'views/App/Portal/LexID/Users/_data_sources/useOrganizationUser';
import useUserRoles from 'views/App/Portal/LexID/Users/_data_sources/useUserRoles';
import useUserUserGroups from 'views/App/Portal/LexID/Users/_data_sources/useUserUserGroups';
import useUserPendingAcknowledgements from 'views/App/Portal/LexID/Users/_data_sources/useUserPendingAcknowledgements';
import useUrlParams from 'hooks/useUrlParams';
import Overview from './Overview';
import Roles from './Roles';
import Groups from './Groups';
import Permissions from './Permissions';
import ManualPermissions from './ManualPermissions';
import PendingAcknowledgements from './PendingAcknowledgements';

type UserTab = {
  label: React.ReactNode;
  key: string;
  component: React.ReactNode;
  count?: number;
};

type UserUrlParams = {
  tab: string;
};

const defaultParams: UserUrlParams = {
  tab: 'overview',
};

export default function Details() {
  const { hasPermission, hasAppPermission, currentOrganizationId } = useContext(AppContext);
  const { id } = useParams();
  const userQuery = useOrganizationUser(currentOrganizationId || '', id || '');
  const [params, setParams] = useUrlParams(defaultParams);
  const userRolesQuery = useUserRoles(
    currentOrganizationId || '',
    id || '',
    {},
    { enabled: !!hasPermission('listUserRoles', currentOrganizationId) }
  );
  const userGroupsQuery = useUserUserGroups(
    currentOrganizationId || '',
    id || '',
    {},
    { enabled: !!hasPermission('listUserGroups', currentOrganizationId) }
  );
  const pendingAcknowledgementsQuery = useUserPendingAcknowledgements(
    id || '',
    {},
    { enabled: hasAppPermission('listManualAcknowledgements') }
  );

  const tabs: { [key: string]: UserTab } = {
    overview: {
      label: t('users.details.tabs.details'),
      key: 'details',
      component: <Overview />,
    },
  };
  if (
    currentOrganizationId &&
    (hasPermission('addUserToRole', currentOrganizationId) ||
      hasPermission('deleteUserFromRole', currentOrganizationId)) &&
    hasPermission('listUserRoles', currentOrganizationId) &&
    hasPermission('listRoles', currentOrganizationId)
  ) {
    tabs.roles = {
      label: t('users.details.tabs.roles'),
      key: 'roles',
      component: <Roles />,
      count: userRolesQuery.data?.roles?.length,
    };
  }

  if (
    currentOrganizationId &&
    (hasPermission('addUserToGroup', currentOrganizationId) ||
      hasPermission('deleteUserFromGroup', currentOrganizationId)) &&
    hasPermission('listUserGroups', currentOrganizationId) &&
    hasPermission('listGroups', currentOrganizationId)
  ) {
    tabs.groups = {
      label: t('users.details.tabs.groups'),
      key: 'groups',
      component: <Groups />,
      count: userGroupsQuery.data?.groups?.length,
    };
  }
  /* if (currentOrganizationId && hasPermission('listUserPermissions', currentOrganizationId)) {
    tabs.permissions = {
      label: t('users.details.tabs.permissions'),
      key: 'permissions',
      component: <Permissions />,
    };
  } */
  if (currentOrganizationId && hasPermission('getUserManualPermissions', currentOrganizationId)) {
    tabs.manuals = {
      label: t('users.details.tabs.manual_permissions'),
      key: 'manual_permissions',
      component: <ManualPermissions />,
    };
  }

  if (hasAppPermission('listManualAcknowledgements')) {
    tabs.assignments = {
      label: t('users.details.tabs.pending_acknowledgements'),
      key: 'pending_acknowledgements',
      component: <PendingAcknowledgements />,
      count: pendingAcknowledgementsQuery.data?.pages?.[0]?.count,
    };
  }

  const updateParams = React.useCallback(
    (updatedParams: UserUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const headerTitle = [userQuery.data?.firstName, userQuery.data?.middleName, userQuery.data?.lastName].join(' ');

  const breadcrumbItems = [
    <Link
      key='b1'
      href='..'
    >
      {t('users.title')}
    </Link>,
    <Typography key='b4'>
      {[userQuery.data?.firstName, userQuery.data?.middleName, userQuery.data?.lastName].join(' ')}
    </Typography>,
  ];

  const editButton =
    currentOrganizationId && hasPermission('updateUser', currentOrganizationId) ? (
      <Button
        variant='contained'
        size='small'
        href={`../edit/${userQuery.data?.id}`}
      >
        {t('users.actions.edit')}
      </Button>
    ) : null;

  return (
    <CommonLayout
      headerProps={{
        title: headerTitle,
        titleBadge:
          userQuery.data?.active !== undefined ? (
            <PhxBadge
              color={userQuery.data?.active ? 'success' : 'error'}
              size='sm'
            >
              {userQuery.data?.active ? 'Active' : 'Inactive'}
            </PhxBadge>
          ) : undefined,
        breadcrumbItems,
        actions: editButton,
      }}
      tabs={{
        items: Object.entries(tabs).map(([key, tab]) => ({
          label: tab.label,
          value: key,
          count: tab.count,
        })),
        value: params.tab,
        onChange: (event: React.SyntheticEvent, newValue: string | number) =>
          updateParams({ tab: newValue.toString() }),
      }}
    >
      {tabs[params.tab]?.component}
    </CommonLayout>
  );
}
